import React, { useEffect, useMemo, useState } from 'react';
import '../styles/Home.css';
import '../styles/Review.css';
import { FaCalendarAlt, FaClock, FaDollarSign, FaEnvelope, FaMapPin, FaPhone } from 'react-icons/fa';
import { FaChevronLeft, FaChevronRight, FaFacebook, FaGlobe, FaInstagram } from 'react-icons/fa6';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { getAmenityIcon } from '../constants/amenityIcons';
import axiosPost from '../helpers/axios-helper';

export default function Experience() {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const [experienceId, setExperienceId] = useState(Number(queryParams.get('id')) || 0);

  const slugify = (str) => {
    try {
      return (str || '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    } catch { return ''; }
  };

  const [review, setReview] = useState({
    id: experienceId,
    type: 'experience',
    title: '',
    heroImage: '',
    contact: {
      address: '',
      phone: '',
      email: '',
      website: ''
    },
    socials: {
      instagram: { handle: '', url: '' },
      facebook: { handle: '', url: '' }
    },
    amenities: [],
    content: '',
    sections: [],
    more: [],
    queerOwned: false,
    price: '',
    duration: ''
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [heroIndex, setHeroIndex] = useState(0);

  const extrasByKey = useMemo(() => ({}), []);

  useEffect(() => {
    // Resolve experienceId from slug if no id provided
    if (!experienceId && slug) {
      const controller = new AbortController();
      axiosPost(
        'experiences/list',
        { 'Content-Type': 'application/json' },
        { page: 1, per_page: 1000 },
        (resp) => {
          const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
          const found = items.find(it => slugify(it.title || '') === slug);
          if (found?.id) setExperienceId(Number(found.id));
        },
        controller
      );
      return () => controller.abort();
    }
    if (!experienceId) return;

    const controller = new AbortController();
    axiosPost(
      'experiences/get',
      { 'Content-Type': 'application/json' },
      { id: experienceId },
      (resp) => {
        if (resp?.success && resp?.data?.experience) {
          const exp = resp.data.experience;
          const extras = Array.isArray(resp.data?.extras) ? resp.data.extras : [];
          const map = {};
          extras.forEach(e => { if (e?.section_key) map[e.section_key] = e; });

          // Derive fields
          const overviewHtml = (map.overview?.description || exp.description || '').trim();
          const heroImage = exp.image || '';
          // Build images array from optional exp.images (newline/comma) plus fallback to single image
          const parseImages = (val) => {
            if (!val) return [];
            if (Array.isArray(val)) return val.map(v => (v ?? '').toString().trim()).filter(Boolean);
            const str = typeof val === 'string' ? val : String(val);
            return str
              .split(/\r?\n|,/)
              .map(s => s.trim())
              .filter(Boolean);
          };
          let images = parseImages(exp.images || '');
          if (!images.length && heroImage) images = [heroImage];
          // Build dynamic sections from extras (excluding 'overview')
          const sections = extras
            .filter(e => (e?.section_key || '').toLowerCase() !== 'overview')
            .filter(e => (e?.description || '').trim() !== '')
            .sort((a, b) => {
              const soA = (a?.sort_order ?? 0);
              const soB = (b?.sort_order ?? 0);
              if (soA !== soB) return soA - soB;
              const ka = (a?.section_key || '').toLowerCase();
              const kb = (b?.section_key || '').toLowerCase();
              return ka.localeCompare(kb);
            })
            .map(e => ({
              key: e.section_key,
              title: (e.title && e.title.trim()) || (e.section_key || '')
                .replace(/_/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase()),
              html: e.description
            }));

          // Optional "more" carousel: related experiences or highlights
          const more = [];

          // Parse amenities value into chips (supports string, array, others)
          const rawAmenities = exp.amenities;
          let amenities = [];
          if (Array.isArray(rawAmenities)) {
            amenities = rawAmenities
              .map(s => (s ?? '').toString().trim())
              .filter(Boolean)
              .map(am => ({ amenity: am }));
          } else if (typeof rawAmenities === 'string') {
            const str = rawAmenities.trim();
            amenities = str
              ? str
                  .split(/\r?\n|,/)
                  .map(s => s.trim())
                  .filter(Boolean)
                  .map(am => ({ amenity: am }))
              : [];
          } else if (rawAmenities != null && rawAmenities !== undefined) {
            const str = String(rawAmenities).trim();
            amenities = str
              ? str
                  .split(/\r?\n|,/)
                  .map(s => s.trim())
                  .filter(Boolean)
                  .map(am => ({ amenity: am }))
              : [];
          }

          setReview({
            id: exp.id,
            type: 'experience',
            title: exp.title || '',
            heroImage,
            images,
            contact: {
              address: exp.address || exp.location || '',
              phone: exp.phone || '',
              email: exp.email || '',
              website: exp.website || ''
            },
            socials: {
              instagram: { handle: '', url: '' },
              facebook: { handle: '', url: '' }
            },
            amenities,
            content: overviewHtml ? `<bold>Overview</bold><br/><br/>${overviewHtml}` : '',
            sections,
            price: exp.price || '',
            duration: exp.duration || '',
            more,
            queerOwned: (Number(exp.queer_owned || 0) === 1) || (exp.queer_owned === true)
          });

          // Redirect legacy /experience?id=... to canonical /experience/:slug
          if (!slug && queryParams.get('id')) {
            const s = slugify(exp.title || '');
            if (s) navigate(`/experience/${s}`, { replace: true });
          }
        }
      },
      controller
    );

    return () => controller.abort();
  }, [experienceId, slug]);

  const nextSlide = () => {
    if (!review.more?.length) return;
    setCurrentIndex((prev) => (prev + 1) % review.more.length);
  };

  const prevSlide = () => {
    if (!review.more?.length) return;
    setCurrentIndex((prev) => (prev - 1 + review.more.length) % review.more.length);
  };

  const nextHero = () => {
    const len = review.images?.length || 0;
    if (len <= 1) return;
    setHeroIndex((prev) => (prev + 1) % len);
  };

  const prevHero = () => {
    const len = review.images?.length || 0;
    if (len <= 1) return;
    setHeroIndex((prev) => (prev - 1 + len) % len);
  };

  return (
    <div style={{ paddingTop: '90px' }}>
      <div className='container'>
        <div className='sponsored-banner'></div>
        <div className='sponsored-title'>Sponsored banner</div>
      </div>

      <div className='container'>
        <div className='review-header'>
          <div className='review-header-info'>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 className='review-title'>{review.title}</h1>
              {review.queerOwned && (
                <div
                  title='Queer Owned'
                  style={{
                    background: 'linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(90deg, #e91e63, #9c27b0) border-box',
                    border: '1px solid transparent',
                    color: '#2c2c2c',
                    padding: '4px 10px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.5px'
                  }}
                >
                  Queer Owned
                </div>
              )}
            </div>
            {(review.contact.address) && (
              <div className='hotel-location'>
                <FaMapPin size={12} color={'#2c2c2c'} />
                <div className='hotel-address'>{String(review.contact.address).toUpperCase()}</div>
              </div>
            )}
          </div>

          {(review.contact.website) && (
            <a className='review-header-btn' href={review.contact.website} target='_blank' rel='noreferrer'>
              BOOK NOW
            </a>
          )}
        </div>
      </div>

      <div className='review-images-container' style={{ position: 'relative', backgroundImage: `url('${(review.images && review.images[heroIndex]) || review.heroImage || ''}')` }}>
        {(review.images?.length > 1) && (
          <>
            <div className='review-content-more-btn' style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)'}} onClick={prevHero}>
              <FaChevronLeft size={16} color='#fff' />
            </div>
            <div className='review-content-more-btn-alt' style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }} onClick={nextHero}>
              <FaChevronRight size={16} color='#fff' />
            </div>
          </>
        )}
      </div>

      <div className='container'>
        <div className='review-content-splitter'>
          <div className='review-content'>
            {/* Experience Details (Price & Duration) */}
            {(review.price || review.duration) && (
              <div className='review-amenities'>
                <div className='review-amenities-title'>EXPERIENCE DETAILS</div>
                <div className='review-amenities-container' style={{ maxWidth: '100%' }}>
                  {review.price && (
                    <div className='ammenity'>
                      <FaDollarSign size={14} color={'#2c2c2c'} />
                      <span>Price: {review.price}</span>
                    </div>
                  )}
                  {review.duration && (
                    <div className='ammenity'>
                      <FaClock size={14} color={'#2c2c2c'} />
                      <span>Duration: {review.duration}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Amenities/Features */}
            {review.amenities?.length > 0 && (
              <div className='review-amenities'>
                <div className='review-amenities-title'>FEATURES</div>
                <div className='review-amenities-container'>
                  {review.amenities.map((am, index) => {
                    const Icon = getAmenityIcon(am.amenity);
                    return (
                      <div className='ammenity' key={index}>
                        <Icon size={14} color={'#2c2c2c'} />
                        <span>{am.amenity}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Overview */}
            {review.content && <div className='review-amenities-title'>Overview</div>}
            {review.content && (
              <div
                style={{ letterSpacing: '1px', fontSize: 14, color: '#2e2e2e', marginBottom: 50, lineHeight: '1.5' }}
                dangerouslySetInnerHTML={{
                  __html: review.content
                    .replace(/<bold>\s*Overview\s*<\/bold>\s*<br\s*\/?>(\s*<br\s*\/?>(\s*<br\s*\/?>)?)?/i, '')
                    .replace(/<bold>/g, '<strong>')
                    .replace(/<\/bold>/g, '<\/strong>')
                }}
              />
            )}

            {/* Dynamic Sections */}
            {review.sections?.map((sec, idx) => (
              <div key={sec.key || idx}>
                <div className='review-amenities-title'>{sec.title}</div>
                <div
                  style={{ letterSpacing: '1px', fontSize: 14, color: '#2e2e2e', marginBottom: 60, lineHeight: '1.5' }}
                  dangerouslySetInnerHTML={{
                    __html: (sec.html || '')
                      .replace(/<bold>/g, '<strong>')
                      .replace(/<\/bold>/g, '</strong>')
                  }}
                />
              </div>
            ))}

            {/* Related Experiences Carousel */}
            {review.more?.length > 0 && (
              <>
                <div className='review-amenities-title'>Related Experiences</div>
                <div className='review-content-more'>
                  <div className='review-content-more-btn' onClick={prevSlide}>
                    <FaChevronLeft size={16} color='#fff' />
                  </div>

                  <div className='review-content-more-main' style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
                    {review.more.map((item, index) => (
                      <div className='review-content-more-item' key={index}>
                        <div className='review-content-more-item-image' style={{ backgroundImage: `url(${item.image})` }} />
                        <div className='review-content-more-item-info'>
                          <div className='review-content-more-item-info-title'>{item.name}</div>
                          <div className='review-content-more-item-info-sub'>{item.about}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className='review-content-more-btn-alt' onClick={nextSlide}>
                    <FaChevronRight size={16} color='#fff' />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className='review-side-content'>
            <div className='review-location-title'>EXPERIENCE LOCATION</div>
            <div className='review-location-map'></div>

            <div className='review-contact-title'>BOOKING INFORMATION</div>
            {review.price && (
              <div className='review-contact-item'>
                <FaDollarSign size={16} color='#2c2c2c' />
                <div className='review-contact-text'>{review.price}</div>
              </div>
            )}
            {review.duration && (
              <div className='review-contact-item'>
                <FaClock size={16} color='#2c2c2c' />
                <div className='review-contact-text'>{review.duration}</div>
              </div>
            )}
            <div className='review-contact-item'>
              <FaCalendarAlt size={16} color='#2c2c2c' />
              <div className='review-contact-text'>Check availability</div>
            </div>

            <div className='review-contact-title'>CONTACT INFORMATION</div>
            {review.contact.phone && (
              <div className='review-contact-item'>
                <FaPhone size={16} color='#2c2c2c' />
                <div className='review-contact-text'>{review.contact.phone}</div>
              </div>
            )}
            {review.contact.email && (
              <div className='review-contact-item'>
                <FaEnvelope size={16} color='#2c2c2c' />
                <div className='review-contact-text'>{review.contact.email}</div>
              </div>
            )}
            {review.contact.website && (
              <div className='review-contact-item'>
                <FaGlobe size={16} color='#2c2c2c' />
                <div className='review-contact-text'>{review.contact.website}</div>
              </div>
            )}

            {(review.socials?.instagram?.url) && (
              <>
                <div className='review-contact-title'>SOCIAL MEDIA</div>
                <a className='review-contact-item' href={review.socials.instagram.url} target='_blank' rel='noreferrer'>
                  <FaInstagram size={16} color='#2c2c2c' />
                  <div className='review-contact-text'>{review.socials.instagram.handle || review.socials.instagram.url}</div>
                </a>
              </>
            )}
            {(review.socials?.facebook?.url) && (
              <a className='review-contact-item' href={review.socials.facebook.url} target='_blank' rel='noreferrer'>
                <FaFacebook size={16} color='#2c2c2c' />
                <div className='review-contact-text'>{review.socials.facebook.handle || review.socials.facebook.url}</div>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
